import { RelatoriosContext } from '@/contexts/RelatoriosContext';
import { AppHeader } from '@/components/shared/AppHeader';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useCallback, useContext, useState } from 'react';
import { useGerarLinkTemporario } from '@/services/General';
import { useAguardarSolicitacao, useGerarRelatorioPDFLote } from '@/services/Relatorios';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Loader2, Check, CalendarDays, History } from 'lucide-react';

import { RelatoriosProvider } from '../RelatoriosProvider';
import { FiltroRelatorios } from './FiltroRelatorios';
import { TabelaRelatorios } from './TabelaRelatorios';
import { Paginacao } from './Paginacao';
import { ModalExportarPorData } from './ModalExportarPorData';
import { ModalHistoricoSolicitacoes } from './ModalHistoricoSolicitacoes';
import { RelatorioApontamentosIbsCbs } from './RelatorioApontamentosIbsCbs';

export function RelatoriosPage() {
  return (
    <RelatoriosProvider>
      <RelatoriosPageInner />
    </RelatoriosProvider>
  );
}

function RelatoriosPageInner() {
  const ctx = useContext(RelatoriosContext);
  if (!ctx) {
    throw new Error('RelatoriosPageInner deve ser usado dentro de <RelatoriosProvider>');
  }

  const {
    empresaSelecionadaNoHeader,
    filtros,
    handleBuscar,
    handleLimparFiltrosGlobais,
    hasEmpresaSelecionadaNoHeader,
  } = ctx;
  const { toast } = useToast();

  const [modalExportarPorDataAberto, setModalExportarPorDataAberto] = useState(false);
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);

  const [agendamentosSelecionados, setAgendamentosSelecionados] = useState<Set<string>>(new Set());
  const [exportando, setExportando] = useState(false);
  const [exportacaoConcluida, setExportacaoConcluida] = useState(false);

  const gerarLinkTemporario = useGerarLinkTemporario();
  const gerarRelatorioPDFLote = useGerarRelatorioPDFLote();
  const { aguardarConclusao } = useAguardarSolicitacao();

  const podeExportar = !exportando;
  const podeExportarPorData = hasEmpresaSelecionadaNoHeader;

  const limparSelecao = () => setAgendamentosSelecionados(new Set());

  const exportarLote = useCallback(async () => {
    if (agendamentosSelecionados.size < 2) {
      toast({
        variant: 'destructive',
        title: 'Seleção insuficiente',
        description: 'Selecione pelo menos 2 agendamentos para exportar em lote.',
      });
      return;
    }

    setExportando(true);

    try {
      const solicitacaoLote = await gerarRelatorioPDFLote.mutateAsync({
        agendamento_ids: Array.from(agendamentosSelecionados).map(Number),
      });

      if (!solicitacaoLote?.solicitacao_id) {
        throw new Error('Não foi possível gerar o PDF em lote.');
      }

      const solicitacao = await aguardarConclusao(solicitacaoLote.solicitacao_id);
      const caminhoArquivo = solicitacao.Caminho_Relatorio;

      if (!caminhoArquivo) {
        throw new Error('Caminho do arquivo inválido.');
      }

      const linkResponse = await gerarLinkTemporario.mutateAsync({
        Funcao: 'GET',
        Bucket: 'imposto-cache',
        Key: caminhoArquivo,
      });

      if (!linkResponse.status?.resultado) {
        throw new Error(
          linkResponse.status?.mensagem || 'Não foi possível gerar o link de download.'
        );
      }

      const urlDownload =
        typeof linkResponse.data === 'string' ? linkResponse.data : linkResponse.data?.url;

      if (!urlDownload) {
        throw new Error('Link de download não retornado pela API.');
      }

      const link = document.createElement('a');
      link.href = urlDownload;
      link.target = '_blank';
      link.rel = 'noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();

      setExportacaoConcluida(true);
      limparSelecao();
      toast({
        title: 'Exportação concluída',
        description: `${agendamentosSelecionados.size} relatório(s) exportado(s) com sucesso.`,
      });
      window.setTimeout(() => setExportacaoConcluida(false), 3000);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Não foi possível exportar os relatórios.';
      toast({
        variant: 'destructive',
        title: 'Erro na exportação',
        description: message,
      });
    } finally {
      setExportando(false);
    }
  }, [
    agendamentosSelecionados,
    aguardarConclusao,
    gerarLinkTemporario,
    gerarRelatorioPDFLote,
    toast,
  ]);

  const handleExportarLote = useCallback(() => {
    exportarLote();
  }, [exportarLote]);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader
        title="Central de Relatórios"
        subtitle="Relatórios fiscais consolidados"
        onBuscar={handleBuscar}
        onLimparFiltros={handleLimparFiltrosGlobais}
        disableModoGrupoEmpresa
        disableFiltroPeriodo
      />

      <main className="w-full px-6 py-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="md:flex md:items-center md:justify-between space-y-4 md:space-y-0">
            <div>
              <h2 className="text-3xl font-bold text-foreground">Central de Relatórios</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Selecione os filtros de empresa e período para consultar os relatórios fiscais
                específicos.
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
              <Button
                onClick={() => setModalHistoricoAberto(true)}
                variant="outline"
                className="w-full md:w-auto"
              >
                <History className="h-4 w-4 mr-2" />
                Histórico de Solicitações
              </Button>
              <Button
                onClick={() => setModalExportarPorDataAberto(true)}
                disabled={!podeExportarPorData}
                variant="outline"
                className="w-full md:w-auto"
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                Exportar Relatórios por Data
              </Button>
              <Button
                onClick={handleExportarLote}
                disabled={!podeExportar}
                className="w-full md:w-auto"
              >
                <AnimatePresence mode="wait">
                  {exportacaoConcluida ? (
                    <motion.div
                      key="check"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1, scale: [1, 1.2, 1, 1.2, 1] }}
                      exit={{ y: -20, opacity: 0 }}
                      transition={{
                        y: { duration: 0.2 },
                        opacity: { duration: 0.2 },
                        scale: { duration: 0.8, times: [0, 0.25, 0.5, 0.75, 1] },
                      }}
                      className="flex items-center"
                    >
                      <Check className="h-5 w-5 mr-2" />
                      Concluído!
                    </motion.div>
                  ) : exportando ? (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center"
                    >
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Exportando...
                    </motion.div>
                  ) : (
                    <motion.div
                      key="default"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Exportar{' '}
                      {agendamentosSelecionados.size > 0 ? `${agendamentosSelecionados.size} ` : ''}
                      PDFs em Lote
                    </motion.div>
                  )}
                </AnimatePresence>
              </Button>
            </div>
          </div>

          <FiltroRelatorios />
          <TabelaRelatorios
            agendamentosSelecionados={agendamentosSelecionados}
            setAgendamentosSelecionados={setAgendamentosSelecionados}
          />
          <Paginacao />

          {/* SPEC-008: relatório do módulo da Reforma, fora do catálogo comum (SPEC-015, decisão 10). */}
          {filtros.operacao !== 'Saída' && (
            <RelatorioApontamentosIbsCbs cnpjContribuinte={empresaSelecionadaNoHeader} />
          )}
        </motion.div>
      </main>

      <ModalExportarPorData
        aberto={modalExportarPorDataAberto}
        onFechar={() => setModalExportarPorDataAberto(false)}
        cnpjContribuinte={empresaSelecionadaNoHeader}
      />

      <ModalHistoricoSolicitacoes
        aberto={modalHistoricoAberto}
        onFechar={() => setModalHistoricoAberto(false)}
      />
    </div>
  );
}
